import { useCallback, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Download, FileSpreadsheet, X } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { ExcelWorkbook, type ExcelCellSelection } from '@/components/excel/excel-workbook';
import { CellAnnotationModal } from '@/components/excel/cell-annotation-modal';
import {
  documentContentUrl,
  fetchDocumentContent,
  useDocumentDetailQuery,
} from '@/lib/api/documents';
import { cn } from '@/lib/utils/cn';

/**
 * T-0115 / E10 — Excel workbook viewer modal (refactor of T-0036).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/modals-xlsx.js`
 * + `design_handoff_caliber_v2/README.md` §4 (modal `data-open-xlsx`),
 * §8 pattern 1 (modal chrome).
 *
 * What this modal does:
 *   - Hosts the existing T-0036 `<ExcelWorkbook>` component (Fortune-Sheet
 *     wrapper) inside the standard modal chrome (header / scrollable body /
 *     footer). The underlying SpreadJS/Fortune-Sheet render + cell-level
 *     annotation flow is preserved verbatim — the modal is just a wrapper.
 *   - Bytes load lazily on `open`, the moment the modal is mounted.
 *   - Cell selection bubbles through `<ExcelWorkbook>`'s `onCellClick`
 *     prop; the "Annotate cell" CTA in the footer opens the existing
 *     `<CellAnnotationModal>` inline-section panel (same component
 *     T-0036 used; not replaced).
 *
 * Accessibility:
 *   - Radix Dialog provides Esc-close, focus-trap, scrim, aria-modal.
 *   - Click-outside dismisses, with a confirm prompt when there's an
 *     un-saved annotation draft (`annotatingSelection` is non-null).
 *
 * Why refactor: T-0036 originally lived on its own page route
 * (`/engagements/:id/documents/:document_id`). The route is preserved
 * for direct linking, but the documents-library card now also opens the
 * workbook inline via this modal — matching the prototype's
 * `data-open-xlsx` opener pattern (README §4).
 */

export interface ExcelWorkbookModalProps {
  engagementId: string;
  documentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExcelWorkbookModal({
  engagementId,
  documentId,
  open,
  onOpenChange,
}: ExcelWorkbookModalProps) {
  const detail = useDocumentDetailQuery(engagementId, documentId);
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [selection, setSelection] = useState<ExcelCellSelection | null>(null);
  const [annotatingSelection, setAnnotatingSelection] = useState<ExcelCellSelection | null>(null);

  // Fetch the workbook bytes only while the modal is open — we don't
  // pre-warm bytes in the background.
  useEffect(() => {
    if (!open) {
      // Reset state when the modal closes so the next open is fresh.
      setBytes(null);
      setLoadError(null);
      setSelection(null);
      setAnnotatingSelection(null);
      return;
    }
    let cancelled = false;
    setBytes(null);
    setLoadError(null);
    fetchDocumentContent(engagementId, documentId)
      .then((buf) => {
        if (!cancelled) setBytes(buf);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [open, engagementId, documentId]);

  // Click-outside / Esc dismiss guard: if the user has an un-committed
  // annotation draft (annotatingSelection !== null), warn before
  // closing. We intercept `onOpenChange(false)` to apply the prompt.
  const requestClose = useCallback(
    (next: boolean) => {
      if (!next && annotatingSelection !== null) {
        const ok =
          typeof window !== 'undefined' && typeof window.confirm === 'function'
            ? window.confirm('Discard the un-saved cell annotation?')
            : true;
        if (!ok) return;
      }
      onOpenChange(next);
    },
    [annotatingSelection, onOpenChange],
  );

  const titleId = `xlsx-modal-title-${documentId}`;
  const name = detail.data?.name ?? 'Workbook';

  return (
    <Dialog.Root open={open} onOpenChange={requestClose}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          className={cn(
            // Excel viewer needs significant real estate — cap at 95vw / 90vh.
            'fixed left-1/2 top-1/2 z-50 grid w-[min(1280px,95vw)] max-h-[92vh] min-h-[60vh]',
            '-translate-x-1/2 -translate-y-1/2 grid-rows-[auto,1fr,auto]',
            'overflow-hidden rounded-modal bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          <ExcelHeader
            titleId={titleId}
            name={name}
            mimeType={detail.data?.mime_type ?? ''}
            byteSize={detail.data?.byte_size ?? 0}
            engagementId={engagementId}
            documentId={documentId}
          />

          <div className="overflow-y-auto px-6 py-4">
            {detail.isLoading || (!bytes && !loadError) ? (
              <div className="flex items-center gap-2 py-12 text-body-sm text-ink-500">
                <Spinner />
                <span>Loading workbook…</span>
              </div>
            ) : loadError ? (
              <div className="space-y-2">
                <ErrorMessage error={loadError} />
                <p className="text-body-sm text-ink-500">
                  The workbook couldn&apos;t be loaded. Use the download link in the header to open
                  it locally.
                </p>
              </div>
            ) : bytes ? (
              <div className="space-y-4">
                {selection ? (
                  <div className="rounded-card border border-ink-100 bg-paper-50 px-4 py-3">
                    <div className="text-meta uppercase tracking-wide text-ink-500">Selection</div>
                    <div className="mt-1 flex flex-wrap items-baseline gap-3 text-body-sm text-ink-900">
                      <span className="font-mono">
                        {selection.sheet_name}!{selection.cell_a1}
                      </span>
                      {selection.value_snapshot !== null ? (
                        <span className="font-mono text-ink-500">{selection.value_snapshot}</span>
                      ) : (
                        <span className="text-ink-500">(empty cell)</span>
                      )}
                    </div>
                  </div>
                ) : null}

                <ExcelWorkbook bytes={bytes} onCellClick={setSelection} />

                {annotatingSelection ? (
                  <div className="rounded-card border-t border-ink-100 pt-4">
                    <CellAnnotationModal
                      engagementId={engagementId}
                      documentId={documentId}
                      selection={annotatingSelection}
                      onSuccess={() => setAnnotatingSelection(null)}
                      onCancel={() => setAnnotatingSelection(null)}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-100 bg-paper-50 px-6 py-3">
            <div className="text-meta text-ink-500">
              {selection ? (
                <>
                  Selected{' '}
                  <span className="font-mono text-ink-700">
                    {selection.sheet_name}!{selection.cell_a1}
                  </span>
                </>
              ) : (
                'Click a cell to select; cell annotations persist as evidence-link objects.'
              )}
            </div>
            <div className="flex items-center gap-2">
              {selection ? (
                <button
                  type="button"
                  onClick={() => setAnnotatingSelection(selection)}
                  disabled={annotatingSelection !== null}
                  className={cn(
                    'inline-flex h-9 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-900',
                    'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                >
                  Annotate cell
                </button>
              ) : null}
              <Dialog.Close
                className={cn(
                  'inline-flex h-9 items-center rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm font-medium text-ink-700',
                  'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
                )}
              >
                Close
              </Dialog.Close>
            </div>
          </div>

          <Dialog.Close
            aria-label="Close workbook"
            className={cn(
              'absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-card',
              'text-ink-500 hover:bg-paper-100 hover:text-ink-900',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
            )}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ExcelHeader({
  titleId,
  name,
  mimeType,
  byteSize,
  engagementId,
  documentId,
}: {
  titleId: string;
  name: string;
  mimeType: string;
  byteSize: number;
  engagementId: string;
  documentId: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-ink-100 px-6 py-4 pr-14">
      <div className="min-w-0 flex items-start gap-3">
        <div
          aria-hidden="true"
          className="flex h-9 w-9 flex-none items-center justify-center rounded-card border border-ink-200 bg-paper-50 text-ink-500"
        >
          <FileSpreadsheet className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <div className="text-meta uppercase tracking-wide text-ink-500">
            Document viewer · spreadsheet · cell comments
          </div>
          <Dialog.Title
            id={titleId}
            className="mt-1 truncate text-h2 font-semibold text-ink-900"
            title={name}
          >
            {name}
          </Dialog.Title>
          <div className="mt-1 text-meta text-ink-500">
            {mimeType || 'workbook'} · {formatBytes(byteSize)}
          </div>
        </div>
      </div>
      <a
        href={documentContentUrl(engagementId, documentId)}
        target="_blank"
        rel="noreferrer"
        download={name}
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-3 text-meta font-medium text-ink-700',
          'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
        )}
      >
        <Download className="h-3.5 w-3.5" aria-hidden="true" />
        <span>Download</span>
      </a>
    </div>
  );
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let idx = 0;
  let val = n;
  while (val >= 1024 && idx < units.length - 1) {
    val /= 1024;
    idx += 1;
  }
  return `${val.toFixed(val >= 100 || idx === 0 ? 0 : 1)} ${units[idx]}`;
}
